import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Builder } from '../../../../../../../../components' // Adjust the import path as needed
import { Button, Icon, Popconfirm, Modal, Form, Input, InputNumber, Tooltip, message } from 'antd'
import StoryCreationForm from './Story/StoryCreationForm'
import { useDispatch, useSelector } from 'react-redux'
import { FaDesktop, FaDownload } from 'react-icons/fa'
import ScenarioHandler from './Scenario'
import * as lessonBuilderActions from '../../../../../../../../store/actions/lessonBuilder'
import nanoid from 'nanoid'
import {
  SortableContainer,
  SortableElement,
  arrayMove
} from 'react-sortable-hoc'
import { AiQuiz } from '../../../../../../../../components/Builder/Elements/EQuiz/internal'
import { useTranslation } from 'react-i18next'

import DeleteIcon from '../../../../../../../../assets/bin.svg'
import IntroOrSummary from './IntroOrSummary'
import BulletPoints from './BulletPoints'
import WorldScenario from './WorldScenario'
import Story from './Story'
import AddContent from './AddContent'
import Infographics from './Infographics'
import { config } from '../../../../../../../../constants'
import { api } from '../../../../../../../../services'
import { fetchAllTokens, fetchRefreshToken } from '../../../../../../../../services/api/user'
import MatchImage from './MatchImage'

const PagePreviewSection = props => {
  const {
    lessonTitle,
    currentPage,
    onPreviewItemClick,
    toPage,
    activeType,
    handleType,
    scenarioId,
    courseId,
    lessonId
  } = props
  const containerRef = useRef(null)
  const isStory = activeType === 'story'
  const isScenario = activeType === 'scenario'
  const isQuizAI = activeType === 'quiz'
  const isIntroduction = activeType === 'introduction'
  const isSummary = activeType === 'summary'
  const isBulletPoints = activeType === 'bulletPoints'
  const isWorldScenario = activeType === 'worldScenario'
  const isAddContent = activeType === 'addContent'
  const isInfographics = activeType === 'infographics'
  const isInteractive = activeType === 'interactive'
  const isMatchImage = activeType === 'matchImage'

  const isSelectable =
    isStory || isScenario || isQuizAI || isWorldScenario || isAddContent || isMatchImage

  const isSinglePageSelectable = isInfographics || isInteractive

  const canSelectPages = isSelectable || isSinglePageSelectable

  const [previewData, setPreviewData] = useState([])
  const [showStoryCreationForm, setShowStoryCreationForm] = useState(false)
  const [showScenario, setShowScenario] = useState(false)
  const [showQuizForm, setShowQuizForm] = useState(false)
  const [showIntroductionForm, setShowIntroductionForm] = useState(false)
  const [showSummaryForm, setShowSummaryForm] = useState(false)
  const [showBulletPointsForm, setShowBulletPointsForm] = useState(false)
  const [showWorldScenario, setShowWorldScenario] = useState(false)
  const [showMatchImage, setShowMatchImage] = useState(false)
  const [showAddContent, setShowAddContent] = useState(false)
  const [showInfographics, setShowInfographics] = useState(false)
  const [showInteractive, setShowInteractive] = useState(false)
  const [isAddingInteractiveQuiz, setIsAddingInteractiveQuiz] = useState(false)

  // Interactive Simulation states
  const [interactiveModalVisible, setInteractiveModalVisible] = useState(false)
  const [simulationTitle, setSimulationTitle] = useState('')
  const [simulationScore, setSimulationScore] = useState(50)
  const [processingScreenCapture, setProcessingScreenCapture] = useState(false)
  const [preparingSimulation, setPreparingSimulation] = useState(false)
  const [refreshToken, setRefreshToken] = useState('')
  const [currentQuiz, setCurrentQuiz] = useState(null)
  const [interactivePageId, setInteractivePageId] = useState(null)

  const pageBuilder = useSelector(state => state.pageBuilder)
  const pageStructureData = useSelector(
    state => state.lessonBuilder.pages_landscape
  )
  const { saving } = useSelector(state => state.lessonBuilder)
  const user = useSelector(state => state.user)

  const courseBuilder = useSelector(state => state.courseBuilder)

  const dispatch = useDispatch()

  const { isShowScenarioModal, content } = pageBuilder
  const scenarioIndex = scenarioId
    ? scenarioId
    : isShowScenarioModal
      ? content
      : null

  useEffect(() => {
    setPreviewData(pageStructureData)
  }, [pageStructureData])

  useEffect(() => {
    const getRefreshToken = async () => {
      const refreshToken = await fetchRefreshToken()
      setRefreshToken(refreshToken)
    }
    getRefreshToken()
  }, [])

  useEffect(() => {
    if (!isStory) {
      setPreviewData(pageStructureData)
    }
  }, [isStory, pageStructureData])



  useEffect(() => {
    if (isBulletPoints) {
      setShowBulletPointsForm(true)
    }
    if (isSummary) {
      setShowSummaryForm(true)
    }
    if (isIntroduction) {
      setShowIntroductionForm(true)
    }
    if (isShowScenarioModal) {
      setShowScenario(true)
    }
  }, [
    isSummary,
    isIntroduction,
    isBulletPoints,
    isShowScenarioModal,
  ])

  const handlePageClick = i => {
    if (isSinglePageSelectable) {
      handleSinglePageSelection(i)
    } else {
      if (isSelectable) {
        handleSelection(i)
      } else {
        onPreviewItemClick(i)
      }
    }
  }

  const handleSinglePageSelection = i => {
    setPreviewData(pre =>
      pre.map((page, index) => {
        const isTargetPage = index === i

        return {
          ...page,
          pageSelected: isTargetPage ? !page.pageSelected : false
        }
      })
    )
  }

  function extractSelectedPageText() {
    // all page must be selectd for introdduction and summary
    const selectedPages = previewData.filter(page =>
      isIntroduction || isSummary ? page.pageSelected : page
    )

    let concatenatedText = ''

    selectedPages.forEach(page => {
      page.data.forEach(element => {
        if (element.type === 'TEXT' && element.display !== 'HEADING') {
          const textContent =
            new DOMParser().parseFromString(element.params.content, 'text/html')
              .body.textContent || ''
          concatenatedText += textContent + ' '
        }
      })
    })

    return concatenatedText
      .replace(/I could not find content related to this topic/g, '') // Remove unwanted phrase
      .replace(/\s+/g, ' ') // Normalize extra spaces
      .trim()
  }

  const extractCurrentPageText = () => {
    const currentPageData = previewData[currentPage - 1]

    let concatenatedText = ''

    currentPageData.data.forEach(element => {
      if (element.type === 'TEXT' && element.display !== 'HEADING') {
        const textContent =
          new DOMParser().parseFromString(element.params.content, 'text/html')
            .body.textContent || ''
        concatenatedText += textContent + ' '
      }
    })

    return concatenatedText
      .replace(/I could not find content related to this topic/g, '') // Remove unwanted phrase
      .replace(/\s+/g, ' ') // Normalize extra spaces
      .trim()
  }

  const extractedText = useMemo(() => {
    return extractSelectedPageText()
  }, [previewData])

  const handleSelection = i => {
    if (containerRef.current) {
      setPreviewData(pre =>
        pre.map((page, index) => ({
          ...page,
          pageSelected:
            index === i ? !page.pageSelected : page.pageSelected || false
        }))
      )
    }
  }

  const canGenerateStory = () => {
    const selectedPages = previewData.filter(page => page.pageSelected)
    return selectedPages.length > 0
  }

  const onClear = () => {
    setShowStoryCreationForm(false)
    setShowScenario(false)
    setShowQuizForm(false)
    setShowIntroductionForm(false)
    setShowSummaryForm(false)
    setShowBulletPointsForm(false)
    setShowWorldScenario(false)
    setShowAddContent(false)
    setShowInteractive(false)
    setShowInfographics(false)
    setInteractiveModalVisible(false)
    handleType(null)
    setShowMatchImage(false)
  }

  const mapOrder = array =>
    array.map((item, index) => ({ ...item, ordering: index + 1 }))

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return
    const { pages, pageStructureData, lessonUpdate } = props
    const newPages = mapOrder(arrayMove(pages, oldIndex, newIndex))
    const newLandscapePages = mapOrder(
      arrayMove(pageStructureData, oldIndex, newIndex)
    )

    setPreviewData(newLandscapePages)

    dispatch(lessonBuilderActions.handleLessonInput('pages', newPages))
    dispatch(
      lessonBuilderActions.handleLessonInput(
        'pages_landscape',
        newLandscapePages
      )
    )
    lessonUpdate({ pages: newPages, pages_landScape: newLandscapePages })
  }

  const handleGenerateButton = () => {
    if (isInfographics) {
      // Auto-select the current page for Infographics to ensure context is correct
      setPreviewData(pre =>
        pre.map((page, index) => ({
          ...page,
          pageSelected: index === currentPage - 1
        }))
      )
      setShowInfographics(true)
    } else if (isStory) {
      setShowStoryCreationForm(true)
    } else if (isScenario) {
      setShowScenario(true)
    } else if (isQuizAI) {
      setShowQuizForm(true)
    } else if (isSummary) {
      setShowSummaryForm(true)
    } else if (isWorldScenario) {
      setShowWorldScenario(true)
    } else if (isAddContent) {
      setShowAddContent(true)
    } else if (isInteractive) {
      setInteractiveModalVisible(true)
    } else if (isMatchImage) {
      setShowMatchImage(true)
    }
  }

  const showLaunchConfirmation = deepLink => {
    let modalInstance = null;

    const LaunchConfirmationContent = () => {
      const [showTooltip, setShowTooltip] = React.useState(false);
      const appLaunchTimeoutRef = React.useRef(null);

      const handleOpenApp = () => {
        // Set up visibility change listener
        const handleVisibilityChange = () => {
          if (document.hidden) {
            // App likely opened successfully, close modal
            clearTimeout(appLaunchTimeoutRef.current);
            if (modalInstance) {
              modalInstance.destroy();
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          }
        };

        // Set up blur listener as backup
        const handleBlur = () => {
          // App likely opened successfully, close modal
          clearTimeout(appLaunchTimeoutRef.current);
          if (modalInstance) {
            modalInstance.destroy();
          }
          window.removeEventListener('blur', handleBlur);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleBlur);

        // Try to launch the app
        window.location.href = deepLink;

        // If app doesn't open within 2 seconds, show tooltip
        appLaunchTimeoutRef.current = setTimeout(() => {
          setShowTooltip(true);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          window.removeEventListener('blur', handleBlur);
        }, 2000);
      };

      React.useEffect(() => {
        return () => {
          if (appLaunchTimeoutRef.current) {
            clearTimeout(appLaunchTimeoutRef.current);
          }
        };
      }, []);

      return (
        <div>
          <div style={{ textAlign: 'center', padding: '20px 0 10px' }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: '#f0f5ff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 20,
              border: '3px solid #d6e4ff'
            }}>
              <FaDesktop style={{ fontSize: 36, color: '#1890ff' }} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 12px 0', color: '#262626' }}>
              {t('Launch Interactive Lesson')}
            </h2>
            <p style={{ color: '#595959', margin: 0, fontSize: 15, lineHeight: '1.6' }}>
              {t("This will open the ShareLook application for building interactive lesson.")}
            </p>
            <p style={{ color: '#8c8c8c', margin: '8px 0 0 0', fontSize: 14, lineHeight: '1.6' }}>
              {t("If the application doesn't open, it may not be installed on your device.")}
            </p>
          </div>

          <div style={{ textAlign: 'center', paddingTop: 20, borderTop: '1px solid #f0f0f0', marginTop: 24 }}>
            <Button
              size="large"
              onClick={() => {
                console.log('User cancelled app launch');
                if (modalInstance) {
                  modalInstance.destroy();
                }
              }}
              style={{
                height: 44,
                borderRadius: 8,
                fontSize: 15,
                minWidth: 100
              }}
            >
              {t('v4:cancel')}
            </Button>
            <Tooltip
              visible={showTooltip}
              title={t('Windows App is not installed')}
              placement="top"
            >
              <Button
                type="primary"
                size="large"
                onClick={handleOpenApp}
                style={{
                  marginLeft: 8,
                  height: 44,
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: 500,
                  minWidth: 140
                }}
              >
                {t('v4:open_sharelook')}
              </Button>
            </Tooltip>
            <Button
              type="default"
              size="large"
              onClick={() => {
                window.open(
                  'https://mj-scorm-bucket.s3.us-east-1.amazonaws.com/windows-screen-capture-app/ELearningSetup.exe',
                  '_blank'
                );
              }}
              style={{
                marginLeft: 8,
                height: 44,
                borderRadius: 8,
                fontSize: 15,
                minWidth: 140
              }}
            >
              <FaDownload style={{ marginRight: 6 }} />
              {t('v4:download_app')}
            </Button>
          </div>
        </div>
      );
    };

    modalInstance = Modal.info({
      title: null,
      icon: null,
      centered: true,
      width: 480,
      okButtonProps: { style: { display: 'none' } },
      content: <LaunchConfirmationContent />
    });
  }

  const showLaunchConfirmation___ = deepLink => {
    const modal = Modal.info({
      title: null,
      icon: null,
      centered: true,
      width: 480,
      okButtonProps: { style: { display: 'none' } },
      content: (
        <div>
          <div style={{ textAlign: 'center', padding: '20px 0 10px' }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: '#f0f5ff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 20,
              border: '3px solid #d6e4ff'
            }}>
              <FaDesktop style={{ fontSize: 36, color: '#1890ff' }} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 12px 0', color: '#262626' }}>
              {t('Launch Interactive Lesson')}
            </h2>
            <p style={{ color: '#595959', margin: 0, fontSize: 15, lineHeight: '1.6' }}>
              {t("This will open the ShareLook application for building interactive lesson.")}
            </p>
            <p style={{ color: '#8c8c8c', margin: '8px 0 0 0', fontSize: 14, lineHeight: '1.6' }}>
              {t("If the application doesn't open, it may not be installed on your device.")}
            </p>
          </div>

          <div style={{ textAlign: 'center', paddingTop: 20, borderTop: '1px solid #f0f0f0', marginTop: 24 }}>
            <Button
              size="large"
              onClick={() => {
                console.log('User cancelled app launch');
                modal.destroy();
              }}
              style={{
                height: 44,
                borderRadius: 8,
                fontSize: 15,
                minWidth: 100
              }}
            >
              {t('v4:cancel')}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                // Try to launch the app but DON'T close the modal
                window.location.href = deepLink;
                // Modal stays open so user can download if app doesn't launch
              }}
              style={{
                marginLeft: 8,
                height: 44,
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 500,
                minWidth: 140
              }}
            >
              {t('v4:open_sharelook')}
            </Button>
            <Button
              type="default"
              size="large"
              onClick={() => {
                window.open(
                  'https://mj-scorm-bucket.s3.us-east-1.amazonaws.com/windows-screen-capture-app/ELearningSetup.exe',
                  '_blank'
                );
                // Keep modal open after download starts
              }}
              style={{
                marginLeft: 8,
                height: 44,
                borderRadius: 8,
                fontSize: 15,
                minWidth: 140
              }}
            >
              <FaDownload style={{ marginRight: 6 }} />
              {t('v4:download_app')}
            </Button>
          </div>
        </div>
      )
    });
  }

  const showLaunchConfirmation_old = (deepLink) => {
    Modal.confirm({
      title: null,
      icon: null,
      centered: true,
      width: 480,
      okText: t('v5:open_sharelook_app'),
      cancelText: t('v4:cancel'),
      okButtonProps: {
        size: 'large',
        style: {
          height: 44,
          borderRadius: 8,
          fontSize: 15,
          fontWeight: 500,
          minWidth: 140
        }
      },
      cancelButtonProps: {
        size: 'large',
        style: {
          height: 44,
          borderRadius: 8,
          fontSize: 15,
          minWidth: 100
        }
      },
      content: (
        <div style={{ textAlign: 'center', padding: '20px 0 10px' }}>
          <div style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: '#f0f5ff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20,
            border: '3px solid #d6e4ff'
          }}>
            <Icon type="rocket" style={{ fontSize: 36, color: '#1890ff' }} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 12px 0', color: '#262626' }}>
            {t('v5:launch_interactive_lesson')}
          </h2>
          <p style={{ color: '#595959', margin: 0, fontSize: 15, lineHeight: '1.6' }}>
            {t('v5:open_the_sharelook_application_for_building_interactive_lesson')}
          </p>
        </div>
      ),
      onOk: () => {
        // Trigger deep link
        window.location.href = deepLink;
      }
    })
  }

  const handleScreenCapture = async () => {
    try {
      // Close the interactive modal and show preparing modal
      setInteractiveModalVisible(false)
      setPreparingSimulation(true)

      const base_Url = config.baseURL.replace(/\/v1\/?$/, '')
      const ai_Url = config.questionAnswerAiUrl

      // Save quiz and get simulation ID
      const simulationId = await saveQuizAndGetId(interactivePageId)

      const userId = user?.info?.id
      const title = simulationTitle
      const score = simulationScore

      const tokens = await fetchAllTokens()
      const idToken = tokens.idToken

      const deepLink = `sharelookapp://open?token=${idToken}&refreshToken=${refreshToken}&base_Url=${base_Url}&ai_Url=${ai_Url}&userId=${userId}&course_Id=${courseId}&isUnityLesson=true&lesson_Id=${lessonId}&page_Id=${currentPage}&title=${encodeURIComponent(title)}&score=${score}&is_simulation=${true}&simulation_id=${simulationId}&simulationMode=desktop&template_id=7`

      // Console log all values
      console.log('=== Deep Link Parameters ===');
      console.log('refreshToken:', refreshToken);
      console.log('idToken:', idToken);
      console.log('base_Url:', base_Url);
      console.log('ai_Url:', ai_Url);
      console.log('userId:', userId);
      console.log('courseId:', courseId);
      console.log('lessonId:', lessonId);
      console.log('currentPage:', currentPage);
      console.log('title:', title);
      console.log('title (encoded):', encodeURIComponent(title));
      console.log('score:', score);
      console.log('simulationId:', simulationId);
      console.log('=== Full Deep Link ===');
      console.log(deepLink);

      // Close preparing modal and show launch confirmation
      setPreparingSimulation(false)
      showLaunchConfirmation(deepLink)
    } catch (error) {
      console.error('Error in handleScreenCapture:', error)
      setPreparingSimulation(false)
      Modal.error({
        title: t('errors:error'),
        content: t('errors:something_went_wrong')
      })
    }
  }

  const removeLessonPage = number => {
    const { pages, pageStructureData, lessonUpdate } = props

    const newPages = pages.filter((page, index) => index !== number)
    const newLandscapePages = pageStructureData.filter(
      (page, index) => index !== number
    )
    console.log('remove page', number, currentPage, newPages, newLandscapePages)

    setPreviewData(newLandscapePages)
    if (currentPage !== 1 && currentPage === number + 1) {
      dispatch(
        lessonBuilderActions.handleLessonInput('currentPage', currentPage - 1)
      )
    } else if (number + 1 < currentPage) {
      dispatch(
        lessonBuilderActions.handleLessonInput('currentPage', currentPage - 1)
      )
    }
    dispatch(lessonBuilderActions.handleLessonInput('pages', newPages))
    dispatch(
      lessonBuilderActions.handleLessonInput(
        'pages_landscape',
        newLandscapePages
      )
    )
    lessonUpdate({ pages: newPages, pages_landScape: newLandscapePages })
  }

  const addNewPage = async (simulationId) => {
    const { pages, pageStructureData, lessonUpdate } = props


    const { backgroundImageContent, backgroundImageId, backgroundImageOpacity, backgroundColor } =
      backgroundInfo


    const newPageData = {
      data: [],
      orientation: 'portrait',
      size: { width: 1024, height: 768 }
    }

    const newData = {
      size: {
        width: 1024,
        height: 768
      },
      orientation: 'landscape',
      data: [
        (backgroundImageId ? {
          uid: nanoid(8),
          type: 'BACKGROUND_IMAGE',
          params: {
            content: backgroundImageContent,
            content_id: backgroundImageId,
            opacity: backgroundImageOpacity
          }
        } : {
          uid: nanoid(8),
          type: 'BACKGROUND_COLOR',
          display: 'BACKGROUND_COLOR',
          params: {
            content: backgroundColor,
            content_id: null,
            opacity: 1
          }
        }),
        {
          uid: nanoid(8),
          type: 'QUIZ',
          params: {
            content: simulationId,
            isSimulation: true,
            x: 0,
            y: 0,
            width: 1023,
            height: 552,
          }
        }
      ]
    }

    let pageIndex = currentPage
    const newPages = [
      ...pages.slice(0, pageIndex),
      newPageData,
      ...pages.slice(pageIndex)
    ]
    const newLandscapePages = [
      ...pageStructureData.slice(0, pageIndex),
      newData,
      ...pageStructureData.slice(pageIndex)
    ]


    console.log({ newLandscapePages, newPages })

    setPreviewData(newLandscapePages)
    const newPageId = pageIndex
    setInteractivePageId(newPageId)
    dispatch(lessonBuilderActions.handleLessonInput('pages', newPages))
    dispatch(
      lessonBuilderActions.handleLessonInput(
        'pages_landscape',
        newLandscapePages
      )
    )
    await lessonUpdate({ pages: newPages, pages_landScape: newLandscapePages })
    handleType(null)

    setIsAddingInteractiveQuiz(false)
  }

  const saveQuizAndGetId = async () => {
    const { lessonId, courseId, pageStructureData: currentPageStructureData } = props

    // Wait a bit to ensure the page is saved
    await new Promise(resolve => setTimeout(resolve, 500))

    const pageId = currentPageStructureData[interactivePageId]?.id

    const payload = {
      title: simulationTitle || 'Interactive Quiz',
      is_restricted: false,
      feedback_correct: '',
      feedback_incorrect: '',
      lesson_id: lessonId,
      course_id: courseId,
      page_id: pageId
    }

    try {
      setIsAddingInteractiveQuiz(true)

      // Create quiz
      const createRes = await api.quizes.quizCreate(payload)
      const simulationId = createRes.data.id

      // Update quiz with simulation data
      const updateRes = await api.quizes.quizUpdate(
        {
          isSimulation: true,
          simulation: {
            title: simulationTitle || 'Interactive Quiz',
            score: simulationScore || 0,
            links: null,
            mode: "from_elearning_app"
          },
        },
        simulationId
      )

      console.log('quizUpdate response:', updateRes)

      // Add simulation to page
      await addNewPage(simulationId)

      return simulationId

    } catch (err) {
      console.error('Error saving quiz:', err)
      setIsAddingInteractiveQuiz(false)
      throw err
    }
  }

  const backgroundInfo = useMemo(() => {
    const templateData = courseBuilder.templateData
    const backgroundImageId = templateData && templateData.background_id
    const backgroundImageLink =
      templateData &&
      templateData.background_img &&
      templateData.background_img.link
    const backgroundImageOpacity = Number(templateData && templateData.opacity)
    const backgroundColor = templateData && templateData.background_color

    return {
      backgroundImageLink,
      backgroundImageId,
      backgroundImageOpacity,
      backgroundColor
    }
  }, [previewData])

  const { t } = useTranslation()

  const PageItem = useMemo(
    () =>
      SortableElement(({ children, className }) => {
        return <div className={className}>{children}</div>
      }),
    [isSelectable]
  )

  const PagesContainer = useMemo(
    () =>
      SortableContainer(({ children, className }) => {
        return (
          <div className={className} ref={containerRef}>
            {children}
          </div>
        )
      }),
    [isSelectable]
  )

  console.log('preview section check background', {
    courseBuilder,
    templateData: courseBuilder.templateData,
    backgroundInfo
  })

  return (
    <div className="page-builder-preview-container">
      <span className="lesson-title">{lessonTitle}</span>
      <PagesContainer
        distance={10}
        onSortEnd={onSortEnd}
        lockAxis="y"
        disableAutoscroll
        lockToContainerEdges
        className="page-builder-preview-list"
        useDragHandle={isSelectable}
      >
        {previewData &&
          previewData.map((page, index) => {
            if (!page) return null

            const currentPageStructure = page.data || []
            const isCurrentPage = !isSelectable && currentPage === index + 1
            const selected = canSelectPages && page.pageSelected

            return (
              <PageItem key={index} index={index} className="page-item">
                <div
                  key={index}
                  className={`page-builder-preview 
            ${isCurrentPage ? 'active' : ''}
            ${selected ? 'selected' : ''}`}
                  onClick={() => {
                    handlePageClick(index)
                  }}
                >
                  <Builder.Page
                    {...props}
                    pageStructure={currentPageStructure}
                    isPreview
                    isAddingInteractiveQuiz={isAddingInteractiveQuiz && interactivePageId === index + 1}
                  />
                  <span className="page-number">{index + 1}</span>

                  {selected && (
                    <span className="selected-check">
                      <Icon type="check" />
                    </span>
                  )}
                </div>
                {/* delete page */}
                {!isSelectable && (
                  <Popconfirm
                    title={t('warnings:delete')}
                    onConfirm={() => removeLessonPage(index)}
                    disabled={saving}
                  >
                    <span className={`delete-page ${saving ? 'disabled' : ''}`}>
                      <img src={DeleteIcon} alt="delete-icon" />
                    </span>
                  </Popconfirm>
                )}
              </PageItem>
            )
          })}
      </PagesContainer>

      {!isSelectable && (
        <div className="page-builder-preview add" onClick={() => toPage('new')}>
          <div className="add-new-page">
            <span>+</span>
            <p>{t('buttons:add_page')}</p>
          </div>
        </div>
      )}

      {canSelectPages && (
        <div className="create-story">
          <Button
            type="green"
            shape="round"
            icon="plus"
            disabled={!canGenerateStory()}
            onClick={handleGenerateButton}
          >
            {isInfographics
              ? t('v5:generate_infographics')
              : isAddContent
                ? t('v4:add_content')
                : isStory
                  ? t('v4:create_story')
                  : isQuizAI
                    ? t('v4:generate_quiz')
                    : isScenario
                      ? t('v3:generate_scenario')
                      : isWorldScenario
                        ? t('v4:generate_world_summary')
                        : isInteractive
                          ? t('v5:interactive_simulation')
                          : isMatchImage
                            ? t('matchimage:title')
                            : t('v4:generate_summary')}
          </Button>
        </div>
      )}

      {/* Interactive Simulation Modal */}
      <Modal
        visible={interactiveModalVisible}
        title={null}
        onCancel={onClear}
        footer={null}
        centered
        width={520}
        bodyStyle={{ padding: '40px 40px 32px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: '#e6f7ff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16
          }}>
            <Icon type="experiment" style={{ fontSize: 32, color: '#1890ff' }} />
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 600, margin: 0, marginBottom: 8 }}>
            {t('v5:create_interactive_lesson')}
          </h2>
          <p style={{ color: '#8c8c8c', margin: 0, fontSize: 14 }}>
            {t('v5:set_up_your_interactive_lesson')}
          </p>
        </div>

        <Form layout="vertical">
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>{t('v5:interactive_lesson_title')}</span>}
            style={{ marginBottom: 24 }}
          >
            <Input
              placeholder={t('v5:enter_interactive_lesson_title')}
              value={simulationTitle}
              onChange={(e) => setSimulationTitle(e.target.value)}
              disabled={processingScreenCapture}
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Tooltip
              title={t('v5:this_feature_available_for_windows_user_only')}
              placement="top"
            >
              <Button
                type="primary"
                size="large"
                icon={processingScreenCapture ? 'loading' : 'camera'}
                onClick={handleScreenCapture}
                loading={processingScreenCapture}
                disabled={processingScreenCapture || !simulationTitle}
                block
                style={{
                  height: 48,
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 500
                }}
              >
                {processingScreenCapture
                  ? t('v4:pleae_wait_processing')
                  : t('v5:create_interactive_lesson')}
              </Button>
            </Tooltip>
          </Form.Item>
        </Form>
      </Modal>

      {/* Preparing Simulation Modal */}
      <Modal
        visible={preparingSimulation}
        title={null}
        footer={null}
        closable={false}
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Icon type="loading" style={{ fontSize: 48, color: '#1890ff', marginBottom: 20 }} />
          <h3 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>
            {t('v5:preparing_interactive_lesson')}
          </h3>
          <p style={{ color: '#8c8c8c', marginTop: 8 }}>
            {t('v5:please_wait_while_we_set_up_your_interactive_lesson')}
          </p>
        </div>
      </Modal>

      {showStoryCreationForm && (
        <Story
          {...props}
          previewData={previewData}
          onCancel={onClear}
          {...backgroundInfo}
        />
      )}

      {showScenario && (
        <ScenarioHandler
          {...props}
          extractedText={extractedText}
          isShowScenarioModal={isShowScenarioModal}
          scenarioId={scenarioIndex}
          lessonId={lessonId}
          courseId={courseId}
          onClear={onClear}
        />
      )}

      {showQuizForm && (
        <AiQuiz
          {...props}
          courseId={courseId}
          previewData={previewData}
          onCancel={onClear}
        />
      )}

      {(showIntroductionForm || showSummaryForm) && (
        <IntroOrSummary
          {...props}
          onCancel={onClear}
          extractedText={extractedText}
          isIntroduction={showIntroductionForm}
          {...backgroundInfo}
        />
      )}

      {/* only extract content from current page */}
      {showBulletPointsForm && (
        <BulletPoints
          {...props}
          onCancel={onClear}
          extractedText={extractCurrentPageText()}
          {...backgroundInfo}
        />
      )}
      {showWorldScenario && (
        <WorldScenario
          {...props}
          onCancel={onClear}
          extractedText={extractedText}
          {...backgroundInfo}
        />
      )}

      {showMatchImage && (
        <MatchImage
          {...props}
          previewData={previewData}
          onCancel={onClear}
          extractedText={extractedText}
        />
      )}

      {showAddContent && (
        <AddContent
          {...props}
          onCancel={onClear}
          previewData={previewData}
          {...backgroundInfo}
        />
      )}

      {showInfographics && (
        <Infographics
          {...props}
          extractedText={extractedText}
          onCancel={onClear}
          setPreviewData={setPreviewData}
          previewData={previewData}
        />
      )}
    </div>
  )
}

export default PagePreviewSection